import { Injectable } from '@angular/core';
import { Firestore, collectionData, docData, query, where } from '@angular/fire/firestore';
import { addDoc, collection, deleteDoc, doc, updateDoc, DocumentReference, getDocs } from '@firebase/firestore';
import { Storage, ref, uploadBytes, getDownloadURL } from '@angular/fire/storage';
import { from, map, Observable, switchMap } from 'rxjs';
import { setDoc } from 'firebase/firestore/lite';

@Injectable({
  providedIn: 'root'
})
export class FirestoreService {

  constructor(
    private fireStore: Firestore,
    private storage: Storage
  ) { }

  getAll(collectionName: string): Observable<any[]> {
    const collectionRef = collection(this.fireStore, collectionName);
    return collectionData(collectionRef, { idField: "id" }) as Observable<any[]>;
  }

  getById(collectionName: string, id: string): Observable<any> {
    const docRef = doc(this.fireStore, `${collectionName}/${id}`);
    return docData(docRef, { idField: "id" }) as Observable<any>;
  }

  add(collectionName: string, data: any): Observable<void> {
    const collectionRef = collection(this.fireStore, collectionName);
    return from(addDoc(collectionRef, data)).pipe(map(() => void 0));
  }

  update(collectionName: string, id: string, data: any): Observable<void> {
    const docRef = doc(this.fireStore, `${collectionName}/${id}`);
    return from(updateDoc(docRef, data));
  }

  delete(collectionName: string, id: string): Observable<void> {
    const docRef = doc(this.fireStore, `${collectionName}/${id}`);
    return from(deleteDoc(docRef));
  }

  uploadFile(path: string, file: File): Observable<string> {
    const storageRef = ref(this.storage, path);
    return from(uploadBytes(storageRef, file)).pipe(
      switchMap(() => getDownloadURL(storageRef))
    );
  }

  getAllByUserId(collectionName: string, userId: string): Observable<any[]> {
    const collectionRef = collection(this.fireStore, collectionName);
    const q = query(collectionRef, where('userId', '==', userId));
    return collectionData(q, { idField: 'id' }) as Observable<any[]>;
  }

  getAdminUserIds(): Observable<string[]> {
    const usersCollection = collection(this.fireStore, 'users');
    const q = query(usersCollection, where('role', '==', 'ADMIN'));
    return collectionData(q, { idField: 'id' }).pipe(
      map((documents: any[]) => documents.map(doc => doc.id))
    );
  }


  getAdminsEmail():Observable<string[]>{
    const usersCollection=collection(this.fireStore,"users");
    const q=query(usersCollection,where("role","==","ADMIN"));
    return collectionData(q,{idField:"id"}).pipe(
      map((document:any[])=>document.map((doc=>doc.email)))
    );
  }
  getUserIds(): Observable<string[]> {
    const usersCollection = collection(this.fireStore, 'users');
    return collectionData(usersCollection, { idField: 'id' }).pipe(
      map((documents: any[]) => documents.map(doc => doc.id))
    );
  }

  getByField(collectionName: string, field: string, value: any): Observable<any[]> {
    const collectionRef = collection(this.fireStore, collectionName);
    const q = query(collectionRef, where(field, '==', value));
    return collectionData(q, { idField: 'id' }) as Observable<any[]>;
  }

  // Overloaded method signatures
  getAllSub(collectionName: string, docId: string, subCollectionName: string): Observable<any[]>;
  getAllSub(collectionName: string, docId: string, subCollectionName: string, subDocId: string, subSubCollectionName: string): Observable<any[]>;

  // Implementation
  getAllSub(collectionName: string, docId: string, subCollectionNameOrSubDocId: string, subDocIdOrUndefined?: string, subSubCollectionName?: string): Observable<any[]> {
    if (subDocIdOrUndefined && subSubCollectionName) {
      // Nested subcollection case
      const subSubCollectionRef = collection(this.fireStore, `${collectionName}/${docId}/${subCollectionNameOrSubDocId}/${subDocIdOrUndefined}/${subSubCollectionName}`);
      return collectionData(subSubCollectionRef, { idField: 'id' }) as Observable<any[]>;
    } else {
      // Single-level subcollection case
      const subCollectionRef = collection(this.fireStore, `${collectionName}/${docId}/${subCollectionNameOrSubDocId}`);
      return collectionData(subCollectionRef, { idField: 'id' }) as Observable<any[]>;
    }
  }

  // Overloaded method signatures
  addSub(collectionName: string, docId: string, subCollectionName: string, data: any): Observable<void>;
  addSub(collectionName: string, docId: string, subCollectionName: string, subDocId: string, subSubCollectionName: string, data: any, customId?: string): Observable<void>;

  // Implementation
  addSub(collectionName: string, docId: string, subCollectionNameOrSubDocId: string, subDocIdOrData: string | any, subSubCollectionNameOrCustomId?: string, dataOrUndefined?: any, customId?: string): Observable<void> {
    if (typeof subDocIdOrData === 'string' && subSubCollectionNameOrCustomId && dataOrUndefined) {
      // Nested subcollection case
      const subSubCollectionRef = collection(this.fireStore, `${collectionName}/${docId}/${subCollectionNameOrSubDocId}/${subDocIdOrData}/${subSubCollectionNameOrCustomId}`);
      if (customId) {
        const docRef = doc(subSubCollectionRef, customId);
        return from(setDoc(docRef, dataOrUndefined)).pipe(map(() => void 0));
      } else {
        return from(addDoc(subSubCollectionRef, dataOrUndefined)).pipe(map(() => void 0));
      }
    } else {
      // Single-level subcollection case
      const subCollectionRef = collection(this.fireStore, `${collectionName}/${docId}/${subCollectionNameOrSubDocId}`);
      return from(addDoc(subCollectionRef, subDocIdOrData)).pipe(map(() => void 0));
    }
  }
}